import { useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import type { BoardToolId } from "../../editor/types";
import { BOARD_TOOL_IDS } from "../../editor/types";
import {
  lineAxisSnapEnabled,
  setLineAxisSnapEnabled,
  subscribeLineAxisSnap,
} from "../../editor/lineSnapPrefs";
import {
  formatKeyCode,
  getToolKeyBindings,
  getToolKeyBinding,
  isAllowedBindingCode,
  resetToolKeyBindings,
  setToolKeyBinding,
  subscribeBoardKeybindings,
  TOOL_KEYBINDING_LABELS,
} from "../../editor/boardKeybindings";
import {
  BOARD_GRID_STYLE_HINTS,
  BOARD_GRID_STYLE_LABELS,
  BOARD_GRID_STYLES,
  getBoardGridStyle,
  setBoardGridStyle,
  subscribeBoardGridStyle,
  type BoardGridStyle,
} from "../../lib/board/boardGridPrefs";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function BoardSettingsDialog({ open, onOpenChange }: Props) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [listeningTool, setListeningTool] = useState<BoardToolId | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const axisSnap = useSyncExternalStore(subscribeLineAxisSnap, lineAxisSnapEnabled, () => true);

  const bindings = useSyncExternalStore(
    subscribeBoardKeybindings,
    getToolKeyBindings,
    getToolKeyBindings
  );

  const gridStyle = useSyncExternalStore(
    subscribeBoardGridStyle,
    getBoardGridStyle,
    () => "grid" as BoardGridStyle
  );

  useEffect(() => {
    if (!open) {
      setListeningTool(null);
      setHint(null);
      return;
    }
    closeRef.current?.focus();
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") {
        if (listeningTool) {
          ev.preventDefault();
          ev.stopPropagation();
          setListeningTool(null);
          setHint(null);
          return;
        }
        onOpenChange(false);
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, listeningTool, onOpenChange]);

  useEffect(() => {
    if (!open || !listeningTool) return;

    function onCapture(ev: KeyboardEvent) {
      ev.preventDefault();
      ev.stopPropagation();

      if (ev.key === "Escape") {
        setListeningTool(null);
        setHint(null);
        return;
      }

      const code = ev.code;
      if (!isAllowedBindingCode(code)) {
        setHint("Эту клавишу нельзя назначить. Попробуйте букву или цифру.");
        return;
      }

      if (ev.ctrlKey || ev.metaKey || ev.altKey) {
        setHint("Для инструментов используйте клавишу без Ctrl / Alt.");
        return;
      }

      setToolKeyBinding(listeningTool, code);
      setListeningTool(null);
      setHint(null);
    }

    window.addEventListener("keydown", onCapture, true);
    return () => window.removeEventListener("keydown", onCapture, true);
  }, [open, listeningTool]);

  if (!open) return null;

  return (
    <div className="wb-board-help wb-board-settings" role="presentation">
      <button
        type="button"
        className="wb-board-help__backdrop"
        aria-label="Закрыть настройки"
        onClick={() => onOpenChange(false)}
      />
      <div
        className="wb-board-help__dialog wb-board-settings__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="wb-board-help__head">
          <h2 id={titleId} className="wb-board-help__title">
            Настройки
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="wb-board-help__close"
            onClick={() => onOpenChange(false)}
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>
        <div className="wb-board-help__body wb-board-settings__body">
          <p className="wb-board-help__lead">
            Назначьте клавиши для инструментов — как в играх. Работает на любой раскладке
            (используется физическая клавиша).
          </p>
          <section className="wb-board-settings__section">
            <h3 className="wb-board-help__section-title">Разлиновка доски</h3>
            <p className="wb-board-settings__hint wb-board-settings__hint--grid">
              Только для вас — другие участники видят свою разлиновку. В экспорт не попадает.
            </p>
            <div className="wb-board-grid-style-picker" role="radiogroup" aria-label="Разлиновка доски">
              {BOARD_GRID_STYLES.map((styleId) => {
                const active = gridStyle === styleId;
                return (
                  <button
                    key={styleId}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`wb-board-grid-style-option${active ? " is-active" : ""}`}
                    onClick={() => setBoardGridStyle(styleId)}
                  >
                    <span
                      className={`wb-board-grid-style-option__preview wb-board-grid-style-option__preview--${styleId}`}
                      aria-hidden="true"
                    />
                    <span className="wb-board-grid-style-option__label">
                      {BOARD_GRID_STYLE_LABELS[styleId]}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="wb-board-settings__hint">{BOARD_GRID_STYLE_HINTS[gridStyle]}</p>
          </section>
          <section className="wb-board-settings__section">
            <h3 className="wb-board-help__section-title">Прямые линии</h3>
            <label className="wb-board-settings__toggle">
              <input
                type="checkbox"
                checked={axisSnap}
                onChange={(e) => setLineAxisSnapEnabled(e.target.checked)}
              />
              <span>Прилипание к горизонтали и вертикали</span>
            </label>
            <p className="wb-board-settings__hint">
              С Shift штрих пера и «Прямая» становятся ровным отрезком. Если угол меньше 5°,
              отрезок выравнивается по горизонтали или вертикали. Выключите, чтобы проводить
              линии с небольшим наклоном. Быстро переключить — клавиша G. Настройка только для вас.
            </p>
          </section>
          <section className="wb-board-settings__section">
            <h3 className="wb-board-help__section-title">Инструменты</h3>
            <ul className="wb-board-settings__list">
              {BOARD_TOOL_IDS.map((toolId) => {
                const code = bindings[toolId] ?? getToolKeyBinding(toolId);
                const listening = listeningTool === toolId;
                return (
                  <li key={toolId} className="wb-board-settings__row">
                    <span className="wb-board-settings__label">{TOOL_KEYBINDING_LABELS[toolId]}</span>
                    <button
                      type="button"
                      className={`wb-board-settings__key${listening ? " is-listening" : ""}`}
                      onClick={() => {
                        setHint(null);
                        setListeningTool(toolId);
                      }}
                    >
                      {listening ? "…" : formatKeyCode(code)}
                    </button>
                  </li>
                );
              })}
            </ul>
            {hint ? <p className="wb-board-settings__hint">{hint}</p> : null}
            {listeningTool ? (
              <p className="wb-board-settings__hint wb-board-settings__hint--active">
                Нажмите клавишу для «{TOOL_KEYBINDING_LABELS[listeningTool]}». Esc — отмена.
              </p>
            ) : null}
          </section>
        </div>
        <footer className="wb-board-help__foot wb-board-settings__foot">
          <button
            type="button"
            className="wb-btn wb-btn--ghost wb-btn--sm"
            onClick={() => {
              resetToolKeyBindings();
              setHint(null);
              setListeningTool(null);
            }}
          >
            Сбросить по умолчанию
          </button>
          <button
            type="button"
            className="wb-btn wb-btn--primary wb-btn--sm"
            onClick={() => onOpenChange(false)}
          >
            Готово
          </button>
        </footer>
      </div>
    </div>
  );
}
